import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  Box,
  Card,
  CardContent,
  CardProps,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';

/** Visual density for KPI cards; `md` matches the original layout. */
export type KpiCardSize = 'sm' | 'md';

export interface KpiBaseCardProps extends Omit<CardProps, 'title'> {
  /** Main KPI label displayed in the header row. */
  title: ReactNode;
  /** Optional content shown to the left of the title (e.g. an icon). */
  headerLeft?: ReactNode;
  /** KPI value highlighted as the primary metric. */
  value: ReactNode;
  /** Optional content aligned to the right side of the header row. */
  headerRight?: ReactNode;
  /** Optional help content shown in a tooltip triggered by a top-right question icon. */
  help?: ReactNode;
  /** Optional content aligned to the right side of the value row. */
  valueRight?: ReactNode;
  /** Optional middle section rendered between value and footer. */
  children?: ReactNode;
  /** Optional footer content; rendered with caption typography and a muted color. */
  footer?: ReactNode;
  /** Layout density; defaults to `md`. */
  size?: KpiCardSize;
}

/**
 * Shared responsive KPI card layout used by all KPI variants.
 * It centralizes spacing/typography so specific cards only define their unique pieces.
 */
export function KpiBaseCard({
  title,
  headerLeft,
  value,
  headerRight,
  help,
  valueRight,
  children,
  footer,
  size = 'md',
  sx,
  ...cardProps
}: KpiBaseCardProps) {
  const isSm = size === 'sm';
  /** `md` uses uniform padding; `sm` uses tighter vertical padding. */
  const contentPaddingMd = { xs: 1.5, sm: 2 };
  const smPaddingX = { xs: 1.25, sm: 1.5 };
  const smPaddingY = { xs: 1, sm: 1.25 };
  const mainStackSpacing = isSm ? 1 : 1.25;
  const headerRowSpacing = isSm ? 1 : 1.5;
  const titleIconSpacing = isSm ? 0.75 : 1;
  const valueRowSpacing = isSm ? 0.75 : 1;
  const valueTypography = isSm ? { xs: 'h6', sm: 'h5' } : { xs: 'h5', sm: 'h4' };
  const helpIconPadding = isSm ? 0.25 : 0.5;

  const headerTrailing =
    headerRight != null || help != null ? (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {headerRight}
        {help != null ? (
          <Tooltip title={help}>
            <IconButton
              size="small"
              color="inherit"
              sx={{ color: 'text.disabled', p: helpIconPadding }}
            >
              <HelpOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    ) : null;

  return (
    <Card
      elevation={1}
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: 2,
        ...sx,
      }}
      {...cardProps}
    >
      <CardContent
        sx={{
          ...(isSm
            ? {
                px: smPaddingX,
                py: smPaddingY,
              }
            : {
                p: contentPaddingMd,
              }),
          height: '100%',
          '&:last-child': {
            pb: isSm ? smPaddingY : contentPaddingMd,
          },
        }}
      >
        <Stack spacing={mainStackSpacing} sx={{ height: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={headerRowSpacing}
            sx={{ minWidth: 0 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={titleIconSpacing}
              sx={{ minWidth: 0, flex: 1 }}
            >
              {headerLeft != null ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    lineHeight: 0,
                  }}
                >
                  {headerLeft}
                </Box>
              ) : null}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={[
                  {
                    textTransform: 'uppercase',
                    letterSpacing: isSm ? '0.07em' : '0.08em',
                    minWidth: 0,
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                  },
                  isSm
                    ? (theme) => ({
                        fontSize: `calc(${theme.typography.caption.fontSize} * 0.88)`,
                      })
                    : {},
                ]}
              >
                {title}
              </Typography>
            </Stack>
            {headerTrailing}
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={valueRowSpacing}
            sx={{ mt: 'auto', minWidth: 0 }}
          >
            <Typography
              color="text.primary"
              sx={{
                typography: valueTypography,
                fontWeight: 600,
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>
            {valueRight}
          </Stack>

          {children}

          {footer != null ? (
            <Typography variant="caption" color="text.disabled" component="div">
              {footer}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
